<script setup lang="tsx">
import type { CondoModalProps } from '.';
import type { SharingOption } from '~/components/ShareContent';
import type { Tab } from '~/components/TabsNavigator';

import {
	ModalPortalModal,
	ShareContent,
	ThumbnailCarousel,
	VideoPlayer
} from '#components';
import { useEvents } from '~/composables/events/useEvents';
import { useModalDetailsCarousel } from '~/composables/modal-details';
import { useShareMenu } from '~/composables/useShareMenu';
import { useTabs } from '~/composables/useTabs';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapMarker from '~/core/service/googleMaps/components/GmapMarker/GmapMarker.vue';
import { MixpanelProps } from '~/core/service/mixpanel';
import { concatUrlPath, getCondoImageAlt } from '~/utils';
import { isMobileDevice } from '~~/utils';
import { CONDO_DETAILS } from '~~/utils/symbols';

import CondoPriceAndInfo from '~/components/condominium/CondoPriceAndInfo/PriceAndInfo.vue';
import CondoTalkToAgentButton from '~/components/condominium/CondoTalkToAgentButton/TalkToAgentButton.vue';
import FixedContactButton from '~/components/FixedContactButton/ContactButton.vue';
import { HeaderTabManagement } from '~/components/Modal/HeaderTabManagement';

import { modalCarouselVariants } from '.';
import { MapCondominiumConfig } from '..';

const props = defineProps<CondoModalProps>();

const condoRef = inject(CONDO_DETAILS);

const condominium = computed(() => {
	if (!condoRef?.value) {
		throw new Error('Condo não foi fornecido via provide');
	}
	return condoRef.value;
});

const condo = computed(() => condominium.value.condo);

const emit = defineEmits<{
	(e: 'click-photo', payload: { photoCount: number; photoUrl: string }): void;
	(e: 'update:tab', payload: Tab): void;
	(e: 'carousel:close'): void;
	(e: 'close'): void;
	(e: 'click-video', payload: string): void;
}>();

const {
	public: { ORIGIN }
} = useRuntimeConfig();

const route = useRoute();
const headerRef = ref();
const shareContentRef = ref<HTMLElement | null>(null);
const shareContentButton = ref<HTMLElement | null>(null);

const { trackShareCondoClicked, trackSharePropertyClicked } = useEvents();

const { isCarouselOpen, currentCarouselItemIndex, imagesEl, showCarousel } =
	useModalDetailsCarousel();

const { currentTab, isCurrentTab, tabs, setCurrentTab } = useTabs({
	tabsConfig: [
		{
			id: 'images' as const,
			label: 'Fotos',
			count: condo.value.images.length,
			inflection: {
				singular: 'foto',
				plural: 'fotos'
			}
		},
		...([
			condo.value.videos.length
				? {
						id: 'videos' as const,
						label: 'Vídeos',
						count: condo.value.videos.length,
						inflection: {
							singular: 'vídeo',
							plural: 'vídeos'
						}
					}
				: {}
		] as TabConfig[]),
		{
			id: 'map' as const,
			label: 'Mapa'
		}
	],
	initialTab: props.tab
});

const { isShareContentVisible, toggleShareContent, share } = useShareMenu({
	shareContentRef,
	shareContentButton
});

const isZoomed = ref(false);
const updateThumbnailZoom = (zoomed: boolean) => {
	isZoomed.value = zoomed;
};

const thumbnailCarouselClasses = computed(() =>
	modalCarouselVariants({ variant: isZoomed.value ? 'zoomedImage' : 'default' })
);

function handleVideoClick(videoUrl: string) {
	emit('click-video', videoUrl);
}

watch(
	() => currentTab.value,
	newValue => {
		emit('update:tab', newValue);
	}
);

const shareUrl = computed<string>(() => concatUrlPath(ORIGIN, route.fullPath));

const getImageAlt = (index: number) =>
	getCondoImageAlt(condo.value.name, index);

function handleShareClicked(option: SharingOption) {
	trackShareCondoClicked({
		share_whatsapp: option.label === 'WhatsApp',
		share_facebook: option.label === 'Facebook',
		copy_link: false,
		share_email: option.label === 'E-mail',
		share_twitter: option.label === 'Twitter'
	});
}

function handlePhotoClick(image: { url: string }, index: number) {
	showCarousel(index);
	const photoUrl = image.url;

	if (!photoUrl) return;

	emit('click-photo', {
		photoUrl: photoUrl,
		photoCount: condo.value.images.length
	});
}

const mapCenter = computed(() => {
	if (condo.value.lat && condo.value.lng) {
		return {
			lat: condo.value.lat,
			lng: condo.value.lng
		};
	}
	return { lat: -23.5835, lng: -46.6792 };
});

function handleShareMobile() {
	share({
		title: condo.value.name,
		url: shareUrl.value
	});

	if (isMobileDevice(window?.navigator?.userAgent)) {
		trackSharePropertyClicked({
			[MixpanelProps.SHARE_WHATSAPP]: false,
			[MixpanelProps.SHARE_FACEBOOK]: false,
			[MixpanelProps.SHARE_EMAIL]: false,
			[MixpanelProps.SHARE_TWITTER]: false,
			[MixpanelProps.COPY_LINK]: false,
			[MixpanelProps.SHARE_CLICKED]: true
		});
	}
}

async function handleGoBack() {
	if (isCarouselOpen.value) {
		isCarouselOpen.value = false;
		return;
	}
	emit('close');
}
</script>
<template>
	<ModalPortalModal
		:open="true"
		:show-close-icon="false"
		content-class="h-full max-w-screen max-h-screen flex flex-col bg-white"
		header-class="font-matter-sq text-xl md:text-2xl font-normal leading-[1.875rem] z-10 tracking-[0.02em] text-left text-primary justify-start items-start h-min pb-0 px-5 pt-5 md:p-5"
		footer-class="hidden"
		@close="emit('close')"
	>
		<template #header>
			<HeaderTabManagement
				ref="headerRef"
				:current-tab="currentTab"
				:tabs="tabs"
				:is-carousel-open="isCarouselOpen"
				:current-carousel-item-index="currentCarouselItemIndex"
				:total-items="condo.images.length"
				:is-share-content-visible="isShareContentVisible"
				@update:current-tab="setCurrentTab"
				@go-back="handleGoBack"
				@toggle-share="toggleShareContent"
				@share-mobile="handleShareMobile"
			>
				<template #share-content>
					<ShareContent
						ref="shareContentRef"
						:model-value="isShareContentVisible"
						variant="dropdown"
						:mail-subject="'Leia mais sobre este condomínio'"
						:content-to-share="shareUrl"
						show-labels
						@update:model-value="toggleShareContent"
						@share-clicked="handleShareClicked"
					/>
				</template>
			</HeaderTabManagement>
		</template>

		<template #default>
			<Container
				v-if="!isCarouselOpen"
				class="flex grow py-5 md:pb-8 md:pt-6"
				:class="{
					'p-0 md:pb-8 md:pt-6': currentTab === 'map',
					'px-0! md:px-0!': isCarouselOpen
				}"
			>
				<div class="flex h-full justify-between gap-3 overflow-y-auto pb-8">
					<div
						class="size-full grow"
						:class="{
							'md:w-2/3': !isCarouselOpen,
							'md:w-full': isCarouselOpen
						}"
					>
						<div
							v-if="isCurrentTab('images') && !isCarouselOpen"
							ref="imagesEl"
							class="grid h-[calc(100dvh-150px)] grid-cols-2 gap-4 overflow-y-auto"
						>
							<NuxtImg
								v-for="(image, index) in condo.images"
								:key="`condo-image-${image.url}`"
								decoding="async"
								loading="lazy"
								width="640"
								height="320"
								sizes="xs:180px sm:280px md:320px lg:420px xl:540px 2xl:640px"
								densities="x1"
								format="webp"
								:src="image.url"
								:placeholder="[50, 25]"
								:alt="getImageAlt(index)"
								class="col-span-1 size-full h-[160px] cursor-pointer object-cover md:h-[320px] nth-[3n]:col-span-2 nth-[3n]:h-[340px]"
								@click="handlePhotoClick(image, index)"
							/>
						</div>
						<div v-if="isCurrentTab('map')" class="h-full">
							<ClientOnly fallback-tag="div">
								<template #fallback>
									<div
										class="bg-map-placeholder-image relative size-full bg-cover bg-no-repeat"
									/>
								</template>

								<GmapMap
									:center="mapCenter"
									:zoom-control="true"
									:zoom="14"
									:min-zoom="14"
									v-bind="MapCondominiumConfig"
									class="bg-map-placeholder-image! size-full"
								>
									<GmapMarker :position="mapCenter" :gmp-draggable="false" />
								</GmapMap>
							</ClientOnly>
						</div>
						<div
							v-if="isCurrentTab('videos')"
							class="flex h-[calc(100dvh-150px)] flex-col gap-5 overflow-y-auto md:gap-6"
						>
							<VideoPlayer
								v-for="video in condo.videos"
								:key="`condo-video-${video}`"
								:src="video"
								class="aspect-video max-h-[600px]"
								@play="handleVideoClick"
							/>
						</div>
					</div>
					<div
						v-show="!isCarouselOpen"
						class="hidden h-fit w-1/3 min-w-[400px] pr-4 md:block md:min-w-[350px]"
					>
						<CondoPriceAndInfo
							:monthly-costs="condominium?.propertyStatistics?.monthlyCosts"
							:price-range="condominium?.propertyStatistics?.priceRange"
							:total-properties="
								condominium?.propertyStatistics?.totalProperties
							"
							:agents="condominium?.agents"
							class="lg:max-w-screen-xs-large top-24 ml-auto md:sticky"
						>
							<CondoTalkToAgentButton
								section-name="card (property details)"
								variant="primary"
								button-classes="w-full border-none px-4 py-3 text-sm leading-7 md:py-3 md:text-base"
							/>
						</CondoPriceAndInfo>
					</div>
				</div>
			</Container>
			<div v-else :class="thumbnailCarouselClasses">
				<ThumbnailCarousel
					v-model:selected-index="currentCarouselItemIndex"
					:images="condo.images"
					:alt-callback="index => getImageAlt(index)"
					@update-zoom="updateThumbnailZoom"
				/>
			</div>
			<FixedContactButton :show="!isCarouselOpen">
				<CondoTalkToAgentButton
					section-name="mobile floating cta"
					size="sm"
					variant="primary"
					button-classes="w-full border-none text-sm leading-7"
				/>
			</FixedContactButton>
		</template>
	</ModalPortalModal>
</template>
